import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { unwrapArray } from '../../lib/api-envelope';
import { formatInstant } from '../../lib/calendar-date';
import { isRefusal, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import {
  MAINTENANCE_FLAGS_ROOT,
  canAnswerMaintenanceFlags,
  useMaintenanceFlags,
  type MaintenanceFlagsSubject,
} from '../../hooks/useMaintenanceFlags';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import type { MaintenanceFlag, MaintenanceFlagSeverity } from '../../types/maintenance-flags';

/**
 * MAINTENANCE CHECKS ON AN ASSET OR A JOB (maintenance-agent brief FE-1, api#944).
 *
 * v1 is SUGGEST: the panel offers, it never edits the asset or the job and it
 * never blocks anything. Resolve means "I've dealt with this"; Dismiss means
 * "this is wrong for this asset" and carries a reason.
 *
 * SHADOW IS INVISIBLE. Outside `suggest` this renders nothing at all: no empty
 * state and no "the agent is watching" line, even if the payload carried rows.
 *
 * Every figure here is the API's. The message is rendered verbatim, and the
 * Evidence column only formats keys the rule put in `evidence` — a key that is
 * absent or null is `—`, never 0.
 *
 * The dismiss dialog uses the standard modal layer (z-50, `lib/layers.ts`). The
 * bottom navs sit at 40 since fe#837, so the z-60 override the billing panel
 * carries is no longer needed.
 */

export const MAINTENANCE_FLAGS_PANEL_ID = 'maintenance-flags-panel';

/**
 * Severity → badge and rail. Exhaustive over emf_severity_check; an unknown
 * value gets a slate dash and a transparent rail, never a guessed severity.
 */
const SEVERITY_SKIN: Record<MaintenanceFlagSeverity, { label: string; fill: string; rail: string }> = {
  critical: { label: 'Critical', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  warn: { label: 'Warn', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  info: { label: 'Info', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
const UNKNOWN_SEVERITY = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
const severitySkin = (s: string) => SEVERITY_SKIN[s as MaintenanceFlagSeverity] ?? UNKNOWN_SEVERITY;

export function SeverityBadge({ severity }: { severity: string }) {
  const skin = severitySkin(severity);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/**
 * A finite number from an evidence value, or null. `Number(null)` is 0, so a
 * null is turned away here before anything converts it.
 */
function evidenceNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

const count = (n: number, unit: string) => `${n} ${n === 1 ? unit : `${unit}s`}`;

/**
 * The one figure a row shows beside the message, read from the rule's own
 * `evidence`. Nothing is computed: each branch picks the key the rule wrote.
 * A flag type with no figure here, or a key that is absent or null, is `—`.
 */
export function flagEvidenceFigure(flag: Pick<MaintenanceFlag, 'flagType' | 'evidence'>): string {
  const ev = flag.evidence ?? {};
  switch (flag.flagType) {
    case 'inspection_due':
    case 'inspection_overdue': {
      // The API's own sentence for an asset with no inspection on record.
      if (ev.neverInspected === true) return 'never inspected in FieldIQ';
      if ('workingDaysSince' in ev) {
        const w = evidenceNumber(ev.workingDaysSince);
        return w === null ? '—' : `${count(w, 'working day')} since inspection`;
      }
      const d = evidenceNumber(ev.daysSince);
      return d === null ? '—' : `${count(d, 'day')} since inspection`;
    }
    case 'cert_expiring': {
      const d = evidenceNumber(ev.daysUntilExpiry);
      return d === null ? '—' : `expires in ${count(d, 'day')}`;
    }
    case 'cert_expired': {
      const d = evidenceNumber(ev.daysSinceExpiry);
      return d === null ? '—' : `expired ${count(d, 'day')} ago`;
    }
    case 'usage_near_limit':
    case 'usage_over_limit': {
      const used = evidenceNumber(ev.counter);
      const limit = evidenceNumber(ev.certDayLimit);
      if (used === null && limit === null) return '—';
      return `${used ?? '—'} of ${limit ?? '—'} cert days`;
    }
    case 'left_on_site_long': {
      const d = evidenceNumber(ev.daysOnSite);
      const limit = evidenceNumber(ev.leftOnSiteDays);
      if (d === null) return '—';
      return `${count(d, 'day')} on site · limit ${limit ?? '—'}`;
    }
    default:
      return '—';
  }
}

const SECONDARY =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-bold text-white transition hover:bg-fieldiq-dark disabled:cursor-not-allowed disabled:opacity-50';

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status;
const apiErrorOf = (err: unknown) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error;

const CONFLICT_LINE = 'This check was already answered or has changed — reloaded.';
const REASON_MIN = 3;
const REASON_MAX = 500;

export function MaintenanceFlagsPanel({ subject }: { subject: MaintenanceFlagsSubject }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const canAnswer = canAnswerMaintenanceFlags(user?.role);

  const query = useMaintenanceFlags(subject, user?.role);
  const failure = queryLoadFailure(query);
  const data = query.data;

  const [rowNotice, setRowNotice] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<MaintenanceFlag | null>(null);
  const [reason, setReason] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);

  // One flag can show on the asset AND on its job, so an answer refreshes both.
  const refresh = () => queryClient.invalidateQueries({ queryKey: MAINTENANCE_FLAGS_ROOT });

  const flags = data?.mode === 'suggest' ? data.flags : [];
  const open = flags.filter(f => f.status === 'open');
  const answered = flags.filter(f => f.status !== 'open');

  // WHO ANSWERED: the flag carries a user id, not a name. A name the roster
  // does not have renders the dash, never the raw id.
  const needsNames = answered.some(f => f.resolvedBy && f.resolvedBy !== user?.id);
  const { data: names } = useQuery({
    queryKey: ['users', 'maintenance-flag-names'],
    queryFn: async () => {
      const rows = unwrapArray<{ id: string; fullName?: string | null }>((await api.users.list()).data);
      return new Map(rows.map(r => [r.id, r.fullName ?? null]));
    },
    enabled: needsNames,
  });
  const whoName = (id: string | null) => {
    if (!id) return '—';
    if (id === user?.id) return 'you';
    return names?.get(id) || '—';
  };

  const resolveMutation = useMutation({
    mutationFn: (flagId: string) => api.maintenanceFlags.resolve(flagId),
    onSuccess: () => {
      setRowNotice(null);
      refresh();
    },
    onError: (err) => {
      setRowNotice(statusOf(err) === 409 ? CONFLICT_LINE : apiErrorOf(err) ?? 'Could not resolve this check.');
      if (statusOf(err) === 409) refresh();
    },
  });

  const dismissMutation = useMutation({
    mutationFn: ({ flagId, text }: { flagId: string; text: string }) => api.maintenanceFlags.dismiss(flagId, text),
    onSuccess: () => {
      closeDismiss();
      refresh();
    },
    onError: (err) => {
      if (statusOf(err) === 409) {
        setDialogError(CONFLICT_LINE);
        refresh();
      } else {
        setDialogError(apiErrorOf(err) ?? 'Could not dismiss this check.');
      }
    },
  });

  const openDismiss = (flag: MaintenanceFlag) => {
    setDismissing(flag);
    setReason('');
    setDialogError(null);
  };
  const closeDismiss = () => {
    setDismissing(null);
    setReason('');
    setDialogError(null);
  };

  // A REFUSAL RENDERS NOTHING: the panel belongs to a role the server says the
  // reader is not. Any other failure is said, in the panel's place, and is
  // never the empty line below.
  if (failure) {
    if (isRefusal(queryFailureError(query))) return null;
    return (
      <PanelShell>
        <LoadFailureNotice error={queryFailureError(query)} subject="maintenance checks" className="px-4 py-6" />
      </PanelShell>
    );
  }

  // Nothing until the mode is known: a shell drawn while loading would show a
  // shadow tenant that a panel exists.
  if (!data || data.mode !== 'suggest') return null;

  const reasonLength = reason.trim().length;
  const reasonValid = reasonLength >= REASON_MIN && reasonLength <= REASON_MAX;

  return (
    <PanelShell header footer={canAnswer ? null : 'Only office staff, supervisors and admins can answer these.'}>
      {rowNotice && (
        <p role="status" className="border-b border-fieldiq-border px-4 py-2 text-sm text-fieldiq-text">
          {rowNotice}
        </p>
      )}

      {flags.length === 0 ? (
        <p className="px-4 py-6 text-sm text-fieldiq-muted">No maintenance checks raised.</p>
      ) : (
        <>
          {open.length > 0 && (
            <>
              {/* md and up: the register table. */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b border-l-4 border-fieldiq-border border-l-transparent text-left text-[0.65rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <th className="px-4 py-2">Severity</th>
                      <th className="px-4 py-2">Check</th>
                      <th className="px-4 py-2">Evidence</th>
                      {canAnswer && <th className="px-4 py-2 text-right"><span className="sr-only">Actions</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {open.map(flag => (
                      <tr
                        key={flag.id}
                        data-testid="maintenance-flag-open"
                        className={`border-b border-l-4 border-fieldiq-border last:border-b-0 ${severitySkin(flag.severity).rail}`}
                      >
                        <td className="px-4 py-3"><SeverityBadge severity={flag.severity} /></td>
                        <td className="px-4 py-3 text-white">{flag.message}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-fieldiq-text">{flagEvidenceFigure(flag)}</td>
                        {canAnswer && (
                          <td className="px-4 py-3">
                            <FlagActions
                              flag={flag}
                              busy={resolveMutation.isPending && resolveMutation.variables === flag.id}
                              onResolve={() => resolveMutation.mutate(flag.id)}
                              onDismiss={() => openDismiss(flag)}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Below md: the same rows as stacked cards, no rail. */}
              <ul className="divide-y divide-fieldiq-border md:hidden">
                {open.map(flag => (
                  <li key={flag.id} className="space-y-2 px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <SeverityBadge severity={flag.severity} />
                      <span className="text-xs text-fieldiq-text">{flagEvidenceFigure(flag)}</span>
                    </div>
                    <p className="text-sm text-white">{flag.message}</p>
                    {canAnswer && (
                      <FlagActions
                        flag={flag}
                        busy={resolveMutation.isPending && resolveMutation.variables === flag.id}
                        onResolve={() => resolveMutation.mutate(flag.id)}
                        onDismiss={() => openDismiss(flag)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {answered.length > 0 && (
            <div className={open.length > 0 ? 'border-t border-fieldiq-border' : ''}>
              <p className="px-4 pt-3 text-[0.65rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                Answered ({answered.length})
              </p>
              <ul className="divide-y divide-fieldiq-border">
                {answered.map(flag => (
                  // Set back by INK, not by opacity: an opacity group fades the
                  // badge and the muted meta line under 4.5 in light theme (fe#898).
                  <li key={flag.id} data-testid="maintenance-flag-answered" className="space-y-1 px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <SeverityBadge severity={flag.severity} />
                      <span className="text-xs text-fieldiq-text">{flagEvidenceFigure(flag)}</span>
                    </div>
                    <p className="text-sm text-fieldiq-text">{flag.message}</p>
                    <p className="text-xs text-fieldiq-muted">
                      {flag.status === 'dismissed' ? 'Dismissed' : flag.status === 'resolved' ? 'Resolved' : '—'} by{' '}
                      {whoName(flag.resolvedBy)} · {formatInstant(flag.resolvedAt, timeZone) ?? '—'}
                    </p>
                    {flag.status === 'dismissed' && (
                      <p className="text-xs text-fieldiq-text">Reason: {flag.dismissReason ?? '—'}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <Dialog open={!!dismissing} onOpenChange={o => { if (!o) closeDismiss(); }}>
        <DialogContent>
          <div role="dialog" aria-modal="true" aria-labelledby="dismiss-maintenance-flag-title">
            <DialogHeader>
              <DialogTitle>
                <span id="dismiss-maintenance-flag-title">Dismiss this check</span>
              </DialogTitle>
            </DialogHeader>
            {dismissing && <p className="mb-3 text-sm text-fieldiq-text">{dismissing.message}</p>}
            <label htmlFor="dismiss-maintenance-flag-reason" className="mb-1 block text-xs font-bold text-white">
              Why is this wrong for this asset?
            </label>
            <textarea
              id="dismiss-maintenance-flag-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={REASON_MAX}
              rows={4}
              autoFocus
              className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
            <p className="mt-1 text-right text-xs text-fieldiq-muted" aria-live="polite">
              {reasonLength} / {REASON_MAX}
            </p>
            {dialogError && <p role="alert" className="mt-2 text-sm text-fieldiq-text">{dialogError}</p>}
            <DialogFooter>
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" className={SECONDARY} onClick={closeDismiss}>Cancel</button>
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!reasonValid || dismissMutation.isPending || dialogError === CONFLICT_LINE}
                  onClick={() => dismissing && dismissMutation.mutate({ flagId: dismissing.id, text: reason.trim() })}
                >
                  {dismissMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  Dismiss check
                </button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </PanelShell>
  );
}

function PanelShell({
  header,
  footer,
  children,
}: {
  header?: boolean;
  footer?: string | null;
  children: React.ReactNode;
}) {
  return (
    <section
      id={MAINTENANCE_FLAGS_PANEL_ID}
      data-testid="maintenance-flags-panel"
      aria-labelledby={header ? `${MAINTENANCE_FLAGS_PANEL_ID}-title` : undefined}
      aria-label={header ? undefined : 'Maintenance checks'}
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      {header && (
        <div className="border-b border-fieldiq-border px-4 py-3">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Maintenance agent</p>
          <h2 id={`${MAINTENANCE_FLAGS_PANEL_ID}-title`} className="mt-1 text-base font-extrabold tracking-tight text-white">
            Maintenance checks
          </h2>
        </div>
      )}
      {children}
      {footer && <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">{footer}</p>}
    </section>
  );
}

function FlagActions({
  flag,
  busy,
  onResolve,
  onDismiss,
}: {
  flag: MaintenanceFlag;
  busy: boolean;
  onResolve: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="flex justify-end gap-2">
      <button type="button" className={SECONDARY} onClick={onResolve} disabled={busy} aria-label={`Resolve: ${flag.message}`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} aria-hidden="true" />}
        Resolve
      </button>
      <button type="button" className={SECONDARY} onClick={onDismiss} aria-label={`Dismiss: ${flag.message}`}>
        <XCircle size={13} aria-hidden="true" />
        Dismiss
      </button>
    </div>
  );
}
